import * as Processor from "@microchip/scf-common/lib/Processor";
import {
    AppModel,
    DerivedData,
    DerivedFunctions,
} from "../generated_module/src/types/AutoModuleTypes";
import { getHexValidator, HexValidator } from "@microchip/scf-validators";
import { TypeValidator } from "@microchip/scf-validators/lib/Validators/Validator";
import { getTypeValidator } from "@microchip/scf-validators/lib/Validators/TypeValidator";
import { getPinsLogic } from "../generated_module/src/pins/PCPHelper";
import { MyComponentNames } from "../generated_module/src/types";
import { BootTemplateInjector } from "./BootTemplateInjector";
import { getters } from "./BootLogic";
import * as helpers from "./Helpers";
import * as initializer_system from "@microchip/initializer-system";
import * as ProjectProperty from "@microchip/project-properties/lib/project-properties";
import * as config_request from "@microchip/config-request";
import {
    Architecture,
    BootUserData,
    BOOT_COMMUNICATION_PROTOCOLS,
    BOOT_MEMORY_PARAMETERS,
    ERASE_CONTROL_VALUE,
    UART_API,
} from "./BootTemplateInterfaces";
import { getKeys } from "@microchip/scf-device/lib/util/Arrays";
import { hasEEPROM } from "./Helpers";
import { architectureValidator, deviceValidator } from "./deviceValidator";

export const getDerivedData = (dataModel: AppModel): DerivedData => {
    if (dataModel) {
        return new MyDerivedData(dataModel);
    }
    return new EmptyDerivedData();
};

//This data will be used at the creator stage and relies on only the state
class EmptyDerivedData implements DerivedData {
    getMyFunctions = (): DerivedFunctions => {
        return {};
    };
    getModel = (): AppModel | undefined => {
        return undefined;
    };
}

//This data will be used at the processor stage
export class MyDerivedData implements DerivedData {
    private dataModel: AppModel;

    // Init state imported data as members of the derived data class
    public eeprom_size: number;
    public deviceName: string;
    public deviceArch: Architecture;
    private memoryInterface: BOOT_MEMORY_PARAMETERS = {
        eeprom_size: 0,
        eeprom_start: 0,
        flash_size: 0,
    };

    constructor(dataModel: AppModel) {
        this.dataModel = dataModel;

        /**
         * Note:
         *      Implementing the state data in this manner allows us to test various data configurations in units
         *      rather than needing ITF for it. It is far easier to achieve unit coverage in this manner than it
         *      would be to attempt to mock all of the API input and output from each module we depend on.
         */
        if (this.getModel() != undefined) {
            this.memoryInterface = {
                eeprom_size:
                    this.getModel().getImportValue("memory_parameters")?.eeprom_size ?? 0,
                eeprom_start:
                    this.getModel().getImportValue("memory_parameters")?.eeprom_start ??
                    0,
                flash_size:
                    this.getModel().getImportValue("memory_parameters")
                        ?.flash_memory_end ?? 0,
            };

            this.deviceName =
                this.getModel().getImportValue("device_meta")?.deviceName ?? "None";
            this.deviceArch =
                this.getModel().getImportValue("device_meta")?.architecture ?? undefined;
        }

        // Set the memory data from the current data just in case memory_parameters failed to configure
        this.eeprom_size = this.memoryInterface.eeprom_size;
    }

    /**
     * @function getMyFunctions
     * @description SCFs default function for getting all the functions that are needed for the redux logical processes
     * @returns JSON object holding function hooks that can be used throughout the redux processing loop
     */
    getMyFunctions = (): DerivedFunctions => {
        return {
            getHelpUrl: this.getDynamicHelp,
            templateData: this.getMyTemplateData,
            alerts: this.getMyAlerts,
            initializer_system_results: this.initializer_system_results,
            importName: this.friendlyImportName,
            importOptionLabel: this.friendlyImportOptionLabels,
            getPinsLogic: getPinsLogic,
            isVisible: this.isComponentVisible,
            bootEndAddressValidator: this.bootEndAddressValidator,
            getCustomUiErrors: this.getCustomUiErrors,
            overrideDefaultValues: this.overrrideDefaultValue,
            getUiBehavior: this.getUiBehavior,
            project_properties_results: this.project_properties_payload,
            config_request_args: (): config_request.Arguments => [
                "bootsizeBootsize",
                "codesizeCodesize",
                "bootendBootend",
                "appendAppend",
            ],
        };
    };

    public getModel = (): AppModel => {
        return this.dataModel;
    };

    /**
     * @function getMyTemplateData
     * @description SCFs default function for getting the template data from your module
     * @returns JSON object holding tokens for template files in key:value pairs
     */
    private getMyTemplateData = (): any => {
        // get the uart interface data
        const UART_IMPORT = this.dataModel?.getImportValue("uart_basic");

        // Load all communication protocols dynamically. *UART only for now*
        const COMMUNICATION_INTERFACES: BOOT_COMMUNICATION_PROTOCOLS = {
            I2C: undefined,
            UART: {
                moduleName: UART_IMPORT?.moduleName,
                Write: helpers.cleanFunctionAPI(UART_IMPORT?.interfaceApi?.api?.Write),
                Read: helpers.cleanFunctionAPI(UART_IMPORT?.interfaceApi?.api?.Read),
                isRxReady: helpers.cleanFunctionAPI(
                    UART_IMPORT?.interfaceApi?.api?.IsRxReady,
                ),
                isTxReady: helpers.cleanFunctionAPI(
                    UART_IMPORT?.interfaceApi?.api?.IsTxReady,
                ),
                TransmitDone: helpers.cleanFunctionAPI(
                    UART_IMPORT?.interfaceApi?.api?.IsTxDone,
                ),
                AutoBaudSet: helpers.cleanFunctionAPI(
                    UART_IMPORT?.interfaceApi?.api?.AutoBaudSet,
                ),
                AutoBaudQuery: helpers.cleanFunctionAPI(
                    UART_IMPORT?.interfaceApi?.api?.AutoBaudQuery,
                ),
                AutoBaudDetectError: "unimplemented",
                AutoBaudReset: "unimplemented",
            } as UART_API,
        };

        // Get the user interface data
        const userInterfaceData: BootUserData = {
            BootRunningIO: this.getModel().getComponentValue("ioPinIndicator"),
            BootEntryIO: this.getModel().getComponentValue("ioPinEntry"),
            IndicatorData: this.getModel().getComponentValue("ioOutPin"),
            EntryData: this.getModel().getComponentValue("ioInPin"),
            FlashRead: this.getModel().getComponentValue("flashRead"),
            ConfigRead: this.getModel().getComponentValue("configRead"),
            ConfigWrite: this.getModel().getComponentValue("configWrite"),
            EERead: this.getModel().getComponentValue("eeDataRead"),
            EEWrite: this.getModel().getComponentValue("eeDataWrite"),
            EraseMode: this.getModel().getComponentValue(
                "eraseType",
            ) as ERASE_CONTROL_VALUE,
            verification: this.getModel().getComponentValue("verificationSelector"),
            ResetVectorAddress: this.getModel().getComponentValue("bootEndAddress") ?? 0,
            SelfProtect: this.getModel().getComponentValue("softwareProtection"),
            Checksum: this.getModel().getComponentValue("supportChecksum"),
        };

        /**
         * Init the BootTemplateInjector by giving it all the needed device info,
         *  communication interface API and user interface data
         * */
        const BootAPI: BootTemplateInjector = new BootTemplateInjector(
            COMMUNICATION_INTERFACES,
            this.getModel().getImportValue("delay_blocking")?.header ?? undefined,
            this.getModel().getImportValue("device_meta")?.deviceName ?? "None",
            this.deviceArch,
            this.memoryInterface,
            userInterfaceData,
        );

        // process the data
        BootAPI.setTemplateData();

        // return the template payload from the Injector
        return BootAPI.inject();
    };

    /**
     * @function getDynamicHelp
     * @param componentName string the name of the component we are editing
     * @param helpUrl string the link that we want to link to
     * @returns the new help url to allow for dynamic component level help
     *
     * @description This function is used to dynamically build the SDL link to
     *  the documentation on whichever verification scheme was chosen by the user
     */
    private getDynamicHelp = (componentName: string, helpUrl: string): string => {
        if (componentName === "verificationSelector") {
            let verify = this.getModel().getComponentValue(componentName);
            if (verify) {
                verify = helpers.replaceAll(verify, " ", ".");
                verify = helpers.replaceAll(verify, "-", "");
                return helpUrl.replace("${verify}", verify);
            }
        }
        return helpUrl;
    };

    /**
     * @function getMyAlerts
     * @description SCFs default function for implementing alerts into the UI
     * @returns an array of alert objects
     */
    private getMyAlerts = (): Processor.Alert[] => {
        const alerts: Processor.Alert[] = [];
        const uartAPI: any =
            this.getModel().getImportValue("uart_basic")?.interfaceApi?.api?.Read ?? "{}";
        let isUartAvail = false;
        const deviceName =
            this.getModel().getImportValue("device_meta")?.deviceName ?? "None";

        const flashReadEnabled = this.getModel().getComponentValue("flashRead");
        const checksumEnabled = this.getModel().getComponentValue("supportChecksum");
        const outPinActive = this.getModel().getComponentValue("ioPinIndicator");
        const inPinActive = this.getModel().getComponentValue("ioPinEntry");
        const configsActivated =
            this.getModel().getComponentValue("configWrite") ||
            this.getModel().getComponentValue("configRead");

        const isSigrowEnabled =
            this.getModel()?.getImportValue("memory_parameters")?.signatureRoutines;
        const isDeviceIdEnabled =
            this.getModel()?.getImportValue("memory_parameters")?.deviceIDRoutines;
        const isConfigEnabled =
            this.getModel()?.getImportValue("memory_parameters")?.configurationRoutines;

        try {
            if (uartAPI !== "{}") {
                isUartAvail = true;
            }
        } catch (e) {
            // do nothing and keep processing
        }

        const bootAddressValid =
            this.getModel().getComponentValue("bootEndAddress") != 0x0;
        const FUSEs: config_request.ProcessedPayload =
            this.getModel().getImportValue("config_request") ?? {};

        let bootFUSE = 0;
        let codeFUSE = 0;

        const keys: string[] = getKeys(FUSEs) ?? Array<string>(0);

        if (
            (FUSEs["bootendBootend"] !== undefined ||
                FUSEs["bootsizeBootsize"] !== undefined) &&
            keys.length > 0
        ) {
            let innerObjKeys = getKeys(FUSEs["bootendBootend"]);
            if (innerObjKeys !== undefined && innerObjKeys.length > 0) {
                bootFUSE = FUSEs["bootendBootend"].defaultValue as number;
                codeFUSE = FUSEs["appendAppend"].defaultValue as number;
            } else {
                innerObjKeys = getKeys(FUSEs["bootsizeBootsize"]);
                bootFUSE = FUSEs["bootsizeBootsize"].defaultValue as number;
                codeFUSE = FUSEs["codesizeCodesize"].defaultValue as number;
            }
        }

        // Print UART Alerts
        if (!isUartAvail) {
            alerts.push({
                type: Processor.AlertTypes.Error,
                text: "Bootloader requires a connection to the UART PLIB. Select a UART version from the 'UART(None)' tab.",
            });
        } else {
            alerts.push({
                type: Processor.AlertTypes.Hint,
                text: "Connection to PLIB was successful. Auto Baud has been enabled by default",
            });
        }

        if (flashReadEnabled) {
            alerts.push({
                type: Processor.AlertTypes.Hint,
                text: "Allowing the bootloader to read the flash space is seen as a security risk in most systems. Consider the impacts of this command on your system.",
            });
        }

        if (
            !checksumEnabled &&
            (architectureValidator.isPIC16(deviceName) ||
                architectureValidator.isPIC18(deviceName))
        ) {
            alerts.push({
                type: Processor.AlertTypes.Warning,
                text: "The Unified Bootloader Host Application calculate a checksum by default. Turning off the checksum support will make UBHA halt after sending the checksum command.",
            });
        }

        // BOOTSIZE Alerts
        const deviceSpecificAlerts = getters.getDeviceSpecificAlerts(
            this.deviceArch,
            deviceName,
            {
                bootFuse: bootFUSE,
                codeFuse: codeFUSE,
                bootAddressValid: bootAddressValid,
            },
        );

        // IO Entry Pin Alerts (Input Pin)
        if (
            inPinActive &&
            helpers.isPinsDataEmpty(this.getModel().getComponentValue("ioInPin"))
        ) {
            alerts.push({
                type: Processor.AlertTypes.Error,
                text: "Select a pin for IO Pin Entry in the Pin Manager: Grid View. 'Pin Grid View' -> BOOT:ENTRY",
            });
        }

        // IO Indicator Pin Alerts (Output Pin)
        if (
            outPinActive &&
            helpers.isPinsDataEmpty(this.getModel().getComponentValue("ioOutPin"))
        ) {
            alerts.push({
                type: Processor.AlertTypes.Error,
                text: "Select a pin for IO Pin Indicator in the Pin Manager: Grid View. 'Pin Grid View' -> BOOT:INDICATE",
            });
        }

        if (architectureValidator.isAVR(this.deviceArch) && !isSigrowEnabled) {
            alerts.push({
                type: Processor.AlertTypes.Warning,
                text: "The bootloader uses the device id by default. Please enable the 'Generate SIGROW APIs' option located in the NVM driver tab",
            });
        } else if (architectureValidator.isPIC18(this.deviceArch) && !isDeviceIdEnabled) {
            alerts.push({
                type: Processor.AlertTypes.Warning,
                text: "The bootloader uses the device id by default. Please enable the 'Generate Device Id APIs' option located in the NVM driver tab",
            });
        } else if (
            architectureValidator.isPIC16(this.deviceArch) &&
            !(isDeviceIdEnabled && isConfigEnabled)
        ) {
            alerts.push({
                type: Processor.AlertTypes.Warning,
                text: "The bootloader uses the device id and configuration area APIs by default. Please enable the 'Generate Device Id APIs' and the 'Generate Configuration Settings APIs' options located in the NVM driver tab",
            });
        }

        // configuration notification
        if (configsActivated) {
            if (architectureValidator.isPIC18(this.deviceArch) && !isConfigEnabled) {
                alerts.push({
                    type: Processor.AlertTypes.Warning,
                    text: "Please enable the 'Generate Configuration Settings APIs' option located in the NVM driver tab in order to read or write to the configuration spaces",
                });
            }
        }

        return alerts.concat(deviceSpecificAlerts);
    };

    /**
     * @function friendlyImportName
     * @param importKey the string name of the import ID for all imported modules
     * @returns The name of the UART Driver needs to be unique. We are using this to set the name of the import
     *
     * @description Our team was told to implement this for the UART DRV interface to function properly
     */
    private friendlyImportName = (importKey: string): string => {
        if (importKey === "uart_basic") {
            return "UART Driver";
        }
        return "";
    };

    /**
     * @function friendlyImportOptionLabels
     * @param importKey the string name of the import ID for all imported modules
     * @param option Processor.Option to get the custom name
     * @returns return the cname of the user or default the return to UART
     *
     * @description Our team was told to implement this for the UART DRV interface to function properly
     */
    public friendlyImportOptionLabels = (
        importKey: string,
        option: Processor.Option,
    ): string => {
        if (importKey === "uart_basic" && option?.payload?.customName) {
            return option.payload.customName;
        }
        return "UART";
    };

    /**
     * @function bootEndAddressValidator
     * @returns {minimum: number, maximum: number}
     * @summary This function is used by the auto module to validate the UI input based on the range given
     */
    private bootEndAddressValidator = (): { minimum: number; maximum: number } => {
        return {
            minimum: 0x0,
            maximum: this.memoryInterface.flash_size,
        };
    };

    /**
     * @function getCustomUiErrors
     * @description SCF function that allows developers to inject their own custom error messages
     * @param componentName the name of the target UI component
     * @returns the error object and actions or undefined if the component was not found
     *
     * @summary We are using this function to verify that the user is
     *      inputing a correctly formated and size number in the bootEndAddress input box
     */
    getCustomUiErrors = (componentName: string): Error[] | undefined => {
        const hexValidator: HexValidator = getHexValidator();
        const typeValidator: TypeValidator = getTypeValidator();
        switch (componentName) {
            case "bootEndAddress":
                return [
                    hexValidator.getMinValidation(">", "0x0"),
                    hexValidator.getMaxValidation(
                        "<",
                        helpers.decimal2Hex(this.memoryInterface.flash_size),
                    ),
                    hexValidator.getFormatValidation(),
                    typeValidator.disable(),
                ];
        }
    };

    /**
     * @function initializer_system_results
     * @description This function sets the priority of the bootloader initialization
     *              function in the initializer-system
     * @returns initializer-system.Results
     */
    private initializer_system_results = (): initializer_system.Results => {
        return {
            initializer: {
                header: {
                    name: "bl_bootload.h",
                    path: "bootloader/",
                },
                name: "BL_Initialize",
                priority: initializer_system.INIT_PRIORITY.END + 100, // 405 priority forces the bootloader to be initialized AFTER the last position in the init_system (400)
            },
        };
    };

    /**
     * @function overrrideDefaultValue
     * @description highjack the default value payload of the target component
     * @param componentName the name of the target component
     * @returns the new overridden default value
     *
     * @summary We are using this function to add the device architecture value to
     *           the UI so we can use it to render the proper UI parts for the device that is used
     */
    private overrrideDefaultValue = (
        componentName: MyComponentNames,
    ): string | undefined => {
        if (componentName == "deviceArch") {
            return this.deviceArch;
        }
    };

    /**
     * @function getUiBehavior
     * @description this function highjacks the payload for all the uiBehavior parts of components
     * @param compName the name of the target component
     * @returns uiBehavior JSON object
     */
    private getUiBehavior = (compName: MyComponentNames): any => {
        if (compName === "verificationSelector") {
            switch (this.getModel().getComponentValue("verificationSelector")) {
                case "Check Reset Vector":
                    return {
                        "ui:help":
                            "This scheme checks the address after the reset vector for a non-null byte value",
                    };
                case "Check State Flag":
                    return {
                        "ui:help":
                            "This scheme will use a status byte to notify the bootloader of a success or a failure",
                    };
                case "Checksum":
                    return {
                        "ui:help":
                            "This scheme uses a Checksum algorithm calculation to validate the application image",
                    };
                case "CRC-16":
                    return {
                        "ui:help":
                            "This scheme uses a Cyclic Redundancy Check algorithm with a 16-bit result to validate the application image.",
                    };
                case "CRC-32":
                    return {
                        "ui:help":
                            "This scheme uses a Cyclic Redundancy Check algorithm with a 32-bit result to validate the application image.",
                    };
            }
        } else if (compName === "bootEndAddress") {
            return {
                "ui:help":
                    "Enter the address of the page boundary where the bootloader section ends. \
                    For example, for a bootloader section of size 400h, enter 0x400. The bootloader \
                    section is presumed to start at 0x0. Note that generated code will fail to link \
                    if this address is lower than the compiled bootloader size. If linking fails, \
                    the user must increase the size of the reserved bootloader space to be larger \
                    than the bootloader itself.",
            };
        }
    };

    /**
     * @function project_properties_args
     * @description Inject all the project properties for the Bootloader application
     * @returns ProjectProperty.Arguments
     */
    private project_properties_payload = (): ProjectProperty.ProcessedPayload => {
        const userAddress = this.dataModel.getComponentValue("bootEndAddress") ?? 0x0;

        let addressRange;

        if (architectureValidator.isPIC16(this.deviceArch) && userAddress > 0x800) {
            addressRange = helpers.parsePIC16ResetVector(userAddress);
        } else {
            addressRange = helpers.parseResetVector(userAddress);
        }

        const ProjectPropertiesArray: ProjectProperty.ProjectProperty[] =
            getters.getConfiguration(this.deviceArch, addressRange);

        return {
            projectProperties: ProjectPropertiesArray,
        };
    };

    /**
     * @function isComponentVisible
     * @param compName the name of the components that appear in the moduleConfig.json
     * @returns false to hide the current component; true to do nothing to the UI
     */
    private isComponentVisible = (compName: MyComponentNames): boolean => {
        const deviceName = this.getModel().getImportValue("device_meta")?.deviceName;

        // Hide the EEPROM sliders if the device does not have an EEPROM
        if (compName === "eeDataRead" || compName === "eeDataWrite") {
            if (!hasEEPROM(this.eeprom_size)) {
                return false;
            }
        }

        // Hide the Config Write slider if the device does not have the functionality
        if (compName === "configWrite") {
            if (!deviceValidator.supportsConfigWrite(deviceName)) {
                return false;
            }
        }

        // else display the component
        return true;
    };
}
